import React from "react";
import ErrorPage from "@UI/ErrorPage/ErrorPage";
import { Button } from "@UI/Button/Button";

/** Detect lazy-load chunk failures (e.g. after deploy when old chunk is gone) */
function isChunkLoadError(error) {
  if (!error) return false;
  const msg = error.message || "";
  const name = error.name || "";
  return (
    name === "ChunkLoadError" ||
    /Loading chunk \d+ failed/i.test(msg) ||
    /Loading CSS chunk \d+ failed/i.test(msg) ||
    /ChunkLoadError/i.test(msg)
  );
}

const chunkErrorContainerStyle = {
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  minHeight: "100vh",
  padding: "2rem",
  fontFamily: "inherit",
};
const chunkErrorTitleStyle = { fontSize: "1.5rem", fontWeight: 600, marginBottom: "0.5rem" };
const chunkErrorDetailStyle = { color: "#6c757d", marginBottom: "1.5rem" };

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMessage: null, isChunkError: false };
    this.reset = this.reset.bind(this);
  }

  static getDerivedStateFromError(error) {
    return {
      hasError: true,
      errorMessage: error?.message ?? null,
      isChunkError: isChunkLoadError(error),
    };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Error caught by ErrorBoundary:", error, errorInfo);
  }

  reset() {
    this.setState({ hasError: false, errorMessage: null, isChunkError: false });
  }

  render() {
    if (this.state.hasError) {
      const { fallback } = this.props;
      if (typeof fallback === "function") {
        return fallback(this.state.errorMessage || "Something went wrong.", this.reset);
      }
      if (this.state.isChunkError) {
        return (
          <div style={chunkErrorContainerStyle}>
            <div style={chunkErrorTitleStyle}>A new version is available</div>
            <div style={chunkErrorDetailStyle}>
              Please refresh the page to continue.
            </div>
            <Button
              label="Refresh page"
              variant="primary"
              onClick={() => window.location.reload()}
            />
          </div>
        );
      }
      return (
        <ErrorPage
          errorMessage={
            this.state.errorMessage || "Oops! Something went wrong."
          }
        />
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
